import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Center,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Spinner,
  Table,
  Tbody,
  Td,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from "@chakra-ui/react";
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Select as MultiSelect } from "chakra-react-select";
import { CherryPayApi } from "../../api/models";
import { Card } from "../../components/Card/Card";
import { DateRangeField } from "../../components/DateRangeField/DateRangeField";
import { ErrorMessage } from "../../components/ErrorMessage/ErrorMessage";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useBusinessContext } from "../../context/ModelContext";
import { useUserContext } from "../../context/UserContext";
import { useApiClient } from "../../hooks/useApiClient";
import { useApiRequest } from "../../hooks/useApiRequest";
import { useToast } from "../../hooks/useToast";
import DateUtil from "../../util/DateUtil";
import {
  DisbursementAccTransactionFilterParams,
  disbursementReferenceTypeOptions,
} from "../../util/buildDisbursementAccTransactionParams";

type AppliedFilters = Omit<DisbursementAccTransactionFilterParams, "timeZone">;

export const CherryPayDisbursementAccTransactions = () => {
  const business = useBusinessContext();
  const [reload, setReload] = useState<number>(0);
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [referenceTypes, setReferenceTypes] = useState<
    CherryPayApi.DisbursementReferenceType[]
  >([]);
  // Filters are applied on Run; Download uses the same applied filters so the CSV matches the table.
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({});
  const { userBusinessId } = useUserContext();
  const { showErrorToast, showSuccessToast } = useToast();

  const api = useApiClient();
  const {
    data: report,
    error: reportError,
    isLoading: reportLoading,
  } = useApiRequest(
    (apiClient) =>
      apiClient.getDisbursementAccTransactionsReport(
        business.BusinessId,
        appliedFilters
      ),
    [business, reload]
  );

  const selectedReferenceTypeOptions = useMemo(
    () =>
      disbursementReferenceTypeOptions.filter((option) =>
        referenceTypes.includes(option.value)
      ),
    [referenceTypes]
  );

  const run = () => {
    setAppliedFilters({
      startDate: DateUtil.setStartEndDate(startDate, "start"),
      endDate: DateUtil.setStartEndDate(endDate, "end"),
      referenceTypes: referenceTypes.length ? referenceTypes : undefined,
    });
    setReload(reload + 1);
  };

  const downloadCsv = async () => {
    const qresult = api.getDisbursementAccTransactionReportCsv(
      business.BusinessId,
      appliedFilters
    );

    qresult.then((result) => {
      if (result.ok) {
        const blobData = new Blob([result.data]);
        const url = URL.createObjectURL(blobData);
        const link = document.createElement("a");
        const formattedDate = DateUtil.getFormattedDownloadDate();
        link.download = `cherrypay-disbursement-account-transactions-${formattedDate}.csv`;
        link.href = url;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (result.statusCode == 404) showErrorToast(result.message);
      else {
        showErrorToast(`Failed to download the report : ${result.message}`);
      }
    });
  };

  const reportRows = useMemo(
    () =>
      report?.Results.map((data) => {
        const occurredAt = DateUtil.getLocalDateFormatFromString(
          data.OccurredAt
        );
        const amount = data.Amount?.toLocaleString("en-AU", {
          style: "currency",
          currency: data.AmountCurrency ?? "AUD",
        });
        const runningBalanceAmount = data.RunningBalanceAmount?.toLocaleString(
          "en-AU",
          {
            style: "currency",
            currency: data.RunningBalanceAmountCurrency ?? "AUD",
          }
        );
        const dataType = `Type: ${data.Type}`;
        return (
          <Tr key={data.TransactionId}>
            <Td>{data.AccountId}</Td>
            <Td>{data.TransactionId}</Td>
            <Td>{occurredAt}</Td>
            <Td>
              <Tooltip label={dataType}>{data.TypeDescription}</Tooltip>
            </Td>
            <Td>{amount}</Td>
            <Td>{runningBalanceAmount}</Td>
            <Td>{data.Reference}</Td>
          </Tr>
        );
      }),
    [report]
  );

  return (
    <>
      <PageHeading>
        <Breadcrumb>
          {!userBusinessId && (
            <BreadcrumbItem>
              <BreadcrumbLink as={Link} to="/businesses">
                Businesses
              </BreadcrumbLink>
            </BreadcrumbItem>
          )}
          <BreadcrumbItem>
            <BreadcrumbLink as={Link} to={`/businesses/${business.BusinessId}`}>
              {business.DisplayName}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink
              as={Link}
              to={`/businesses/${business.BusinessId}/reports`}
            >
              Reports
            </BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>
        <PageHeading.Title>
          CherryPay Disbursement Account Transactions Report
        </PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack align="start" width="100%">
          <Card>
            <Flex gridGap={6} className="form-filter-wrapper">
              <Box minW="320px">
                <DateRangeField
                  label="Date"
                  startDate={startDate}
                  endDate={endDate}
                  onStartDateChange={setStartDate}
                  onEndDateChange={setEndDate}
                />
              </Box>
              <Box minW="320px">
                <FormControl>
                  <FormLabel>Reference Type</FormLabel>
                  <MultiSelect
                    isMulti
                    placeholder="All reference types"
                    options={disbursementReferenceTypeOptions}
                    value={selectedReferenceTypeOptions}
                    onChange={(selectedOptions) =>
                      setReferenceTypes(
                        selectedOptions?.map((option) => option.value) ?? []
                      )
                    }
                    closeMenuOnSelect={false}
                    selectedOptionColor="blue"
                    chakraStyles={{
                      control: (provided) => ({
                        ...provided,
                        bg: "white",
                      }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        bg: "white",
                      }),
                      indicatorSeparator: () => ({
                        display: "none",
                      }),
                      multiValue: (provided) => ({
                        ...provided,
                        borderRadius: "full",
                      }),
                    }}
                  />
                </FormControl>
              </Box>
            </Flex>
            <HStack marginTop="4">
              <Button colorScheme="cherryButton" onClick={run}>
                Run
              </Button>
              {/* Download uses the applied (last Run) filters; disable it until those rows are on screen so the CSV matches. */}
              <Button
                onClick={downloadCsv}
                isDisabled={reportLoading || !!reportError}
              >
                Download
              </Button>
            </HStack>
          </Card>
          <Card>
            {reportLoading && (
              <Center>
                <Spinner margin="4" />
              </Center>
            )}
            {!reportError && (
              <Table width="100%" size="sm">
                <Thead>
                  <Tr>
                    <Td>Account ID</Td>
                    <Td>Transaction ID</Td>
                    <Td>Date</Td>
                    <Td>Description</Td>
                    <Td>Amount</Td>
                    <Td>Running Balance</Td>
                    <Td>Reference</Td>
                  </Tr>
                </Thead>
                <Tbody>
                  {reportRows}
                  {!reportLoading && report && report.Results.length === 0 && (
                    <Tr>
                      <Td colSpan={7} fontStyle="italic" textAlign="center">
                        No transactions found
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            )}
            {reportError && (
              <ErrorMessage>
                An error was encountered while getting report. {reportError}
              </ErrorMessage>
            )}
          </Card>
        </VStack>
      </PageContent>
    </>
  );
};
